import { apiSlice } from './apiSlice';

export interface Workspace {
  id: string;
  name: string;
  slug: string;
  brandColor: string;
  accentColor: string;
  logoUrl: string | null;
  metadata?: Record<string, string>;
  role?: 'admin' | 'manager' | 'viewer';
  createdAt: string;
  updatedAt: string;
}

export interface CreateWorkspaceRequest {
  name: string;
  slug?: string;
  brandColor?: string;
  accentColor?: string;
}

export interface UpdateWorkspaceRequest {
  name?: string;
  slug?: string;
}

export interface UpdateBrandingRequest {
  brandColor?: string;
  accentColor?: string;
  logo?: File;
}

export const workspaceApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // List user's workspaces
    getWorkspaces: builder.query<{ items: Workspace[] }, void>({
      query: () => '/workspaces',
      providesTags: (result) =>
        result
          ? [
              ...result.items.map(({ id }) => ({ type: 'Workspaces' as const, id })),
              { type: 'Workspaces', id: 'LIST' },
            ]
          : [{ type: 'Workspaces', id: 'LIST' }],
    }),

    // Get workspace by ID
    getWorkspaceById: builder.query<Workspace, string>({
      query: (id) => `/workspaces/${id}`,
      providesTags: (result, error, id) => [{ type: 'Workspaces', id }],
    }),

    // Create workspace
    createWorkspace: builder.mutation<Workspace, CreateWorkspaceRequest>({
      query: (data) => ({
        url: '/workspaces',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Workspaces', id: 'LIST' }],
    }),

    // Update workspace
    updateWorkspace: builder.mutation<Workspace, { id: string; data: UpdateWorkspaceRequest }>({
      query: ({ id, data }) => ({
        url: `/workspaces/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Workspaces', id },
        { type: 'Workspaces', id: 'LIST' },
      ],
    }),

    // Delete workspace
    deleteWorkspace: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/workspaces/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Workspaces', id },
        { type: 'Workspaces', id: 'LIST' },
      ],
    }),

    // Update workspace branding
    updateWorkspaceBranding: builder.mutation<
      Workspace,
      { id: string; data: UpdateBrandingRequest }
    >({
      query: ({ id, data }) => {
        const formData = new FormData();
        if (data.brandColor) formData.append('brandColor', data.brandColor);
        if (data.accentColor) formData.append('accentColor', data.accentColor);
        if (data.logo) formData.append('logo', data.logo);

        return {
          url: `/workspaces/${id}/branding`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: 'Workspaces', id },
        { type: 'Workspaces', id: 'LIST' },
      ],
    }),

    // Switch workspace context
    switchWorkspace: builder.mutation<
      { workspace: Workspace; role: string; token: string },
      string
    >({
      query: (id) => ({
        url: `/workspaces/${id}/switch`,
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useGetWorkspacesQuery,
  useGetWorkspaceByIdQuery,
  useCreateWorkspaceMutation,
  useUpdateWorkspaceMutation,
  useDeleteWorkspaceMutation,
  useUpdateWorkspaceBrandingMutation,
  useSwitchWorkspaceMutation,
} = workspaceApi;
